/* eslint-disable react-hooks/exhaustive-deps */
import axios from 'axios';
import PropTypes from 'prop-types';
import React, { createContext, useEffect, useReducer, useState, useCallback } from 'react';
import { debounce, isEmpty } from 'lodash';
import Reducer from './Reducer';
import {
  getSketchData,
  getUrlSearchParams,
  getMatchSketches,
  getFileName,
  trans,
  viewsMap,
  localStorageItemName,
} from './utils';

const initialState = {
  productData: {},
  colorData: {},
  productMods: [],
  selectedColor: null,
  selectedMarking: {},
  selectedMods: {},
  selectedView: viewsMap.FRONT,
  userData: {},
};

const urlParams = getUrlSearchParams();
const forPdf = urlParams.get('forPdf') === 'true';
const productID = urlParams.get('product');
const hashID = urlParams.get('id');

const Store = ({ children }) => {
  const [state, dispatch] = useReducer(Reducer, initialState);
  const [isLoading, setIsLoading] = useState(true);
  const [isModsLoaded, setIsModsLoaded] = useState(false);

  const hash = () => (forPdf ? hashID : localStorage.getItem(localStorageItemName));

  const putData = (productData, colorData, selectedColor, selectedMarking, selectedMods, userData) => {
    const vendoCodes = [productData.vendo_code.slice(0, -2), productData.vendo_code];
    const sketches = [];

    for (const sketch of Object.values(colorData.sketches || {})) {
      if (sketch.image) {
        sketches.push(sketch.image);
      }
    }
    for (const mod of Object.values(selectedMods || {})) {
      if (mod.sketches && mod.sketches.length) {
        const matchSketches = mod.sketches.filter(sketch => {
          const sketchInfo = getSketchData(getFileName(sketch));
          return vendoCodes.includes(sketchInfo.matchCode);
        });
        sketches.push(...matchSketches);
      }
    }

    const config = {
      productData: { id: productData.id },
      selectedColor,
      selectedMarking,
      selectedMods,
      userData,
      sketches,
    };
    const data = {
      product: productID,
      config: JSON.stringify(config),
    };
    try {
      axios.put(`/api/configurator_config/${hash()}/`, data, {
        headers: { 'Content-Type': 'application/json' },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const debounceSaveData = useCallback(
    debounce((productData, colorData, selectedColor, selectedMarking, selectedMods, userData) => {
      putData(productData, colorData, selectedColor, selectedMarking, selectedMods, userData);
    }, 750),
    [],
  );

  const debounceSetIsLoading = useCallback(
    debounce(value => {
      setIsLoading(value);
    }, 150),
    [],
  );

  const getHash = async () => {
    try {
      const response = await axios.post(
        '/api/configurator_config/',
        { product: productID },
        {
          headers: { 'Content-Type': 'application/json' },
        },
      );
      localStorage.setItem(localStorageItemName, response.data.hash);
    } catch (error) {
      console.error(error);
    }
  };

  const getSavedData = async () => {
    try {
      debounceSetIsLoading(true);
      const response = await axios.get(`/api/configurator_config/${hash()}/`);
      const savedData = typeof response.data.config === 'string' ? JSON.parse(response.data.config) : {};
      const productData = await axios.get(`/api/configurator_products/${response.data.product}/`);
      let colorSketches = [];

      if (!isEmpty(savedData?.productData)) {
        if (savedData.productData?.id !== response.data.product) {
          const colorData = await axios.get(`/api/configurator_products/${savedData.productData.id}/`);
          colorSketches = colorData.data.sketches;
        }
        const payload =
          colorSketches.length > 0
            ? { ...productData.data, id: savedData.productData.id, sketches: colorSketches }
            : productData.data;
        dispatch({ type: 'SET_PRODUCT_DATA', payload });
      } else {
        dispatch({ type: 'SET_PRODUCT_DATA', payload: productData.data });
      }

      if (savedData?.selectedColor) {
        dispatch({ type: 'SET_SELECTED_COLOR', payload: savedData.selectedColor });
      }
      if (!isEmpty(savedData?.selectedMarking)) {
        dispatch({ type: 'SET_SELECTED_MARKING', payload: savedData.selectedMarking });
      }
      if (!isEmpty(savedData?.selectedMods)) {
        dispatch({ type: 'SET_SELECTED_MODS', payload: savedData.selectedMods });
      }
      if (!isEmpty(savedData?.userData)) {
        dispatch({ type: 'SET_USER_DATA', payload: savedData.userData });
      }
    } catch (error) {
      console.error(error);
    } finally {
      debounceSetIsLoading(false);
    }
  };

  const getProductData = async (id = productID, getOnlyColorData = false) => {
    debounceSetIsLoading(true);
    try {
      const response = await axios.get(`/api/configurator_products/${id}/`);
      const payload = getOnlyColorData
        ? { ...state.productData, sketches: response.data.sketches, id }
        : {
            id,
            available_colors: response.data.available_colors,
            color: response.data.color,
            lines: response.data.lines,
            marking: response.data.marking,
            modifications: response.data.modifications,
            name: response.data.name,
            sketches: response.data.sketches,
            size: response.data.size,
            vendo_code: response.data.vendo_code,
          };
      dispatch({ type: 'SET_PRODUCT_DATA', payload });
    } catch (error) {
      console.error(error);
    } finally {
      debounceSetIsLoading(false);
    }
  };

  const getColorData = () => {
    if (!state.productData?.available_colors) {
      return;
    }
    const color = state.productData.available_colors?.find(color => color.id === state.productData.id);

    const sketches = state.productData.sketches?.reduce((acc, sketch) => {
      const viewName = getSketchData(sketch.image_original_name).view;
      acc[viewName] = { name: viewName, image: sketch.image };
      return acc;
    }, {});

    const payload = {
      id: state.productData.id,
      color,
      sketches,
    };

    dispatch({ type: 'SET_COLOR_DATA', payload });
    if (!state.selectedColor || state.selectedColor !== state.productData.id) {
      dispatch({ type: 'SET_SELECTED_COLOR', payload: state.productData.id });
    }
  };

  const getData = async () => {
    if (hash()) {
      getSavedData();
    } else {
      await getProductData(productID);
      getHash();
    }
  };

  const setDefaultMods = useCallback(() => {
    if (!isEmpty(state.selectedMods)) {
      setIsModsLoaded(true);
      return;
    }
    const payload = state.productMods.reduce((acc, modGroup) => {
      if (modGroup.isRequired) {
        const defaultMod = modGroup.items[0];
        const vendoCode = state.productData.vendo_code.slice(0, -2);
        acc[modGroup.id] = {
          id: defaultMod.id,
          name: defaultMod.name,
          vendoCode: defaultMod.vendoCode,
          sketches: getMatchSketches(defaultMod, vendoCode),
          topLayer: modGroup.topLayer,
        };
      }
      return acc;
    }, {});

    dispatch({ type: 'SET_SELECTED_MODS', payload });
    setIsModsLoaded(true);
  }, [state.selectedMods, state.productData.vendo_code, state.productMods]);

  const setProductMods = () => {
    if (!state.productData?.modifications) {
      return;
    }
    const itemModifications = state.productData?.modifications?.reduce((acc, modification) => {
      const mod = {
        id: modification.id,
        name: modification.name,
        price: Number(modification.price)?.toFixed(2) ?? '0.00',
        quantity: 1,
        maxQuantity: modification.max_number_of_addons > 0 ? modification.max_number_of_addons : 1,
        group: modification.modification_group,
        image: modification.main_image,
        sketches: modification.sketches,
        vendoCode: modification.vendo_code,
      };

      if (mod.group) {
        const key = mod.group.id;
        const isRequired = Number(mod.price) <= 0.01;

        if (!acc[key]) {
          acc[key] = {
            id: key,
            name: mod.group.name,
            items: [],
            isRequired,
            topLayer: mod.group.vendo_id === 1826,
          };
        }
        acc[key].items.push(mod);
      }
      return acc;
    }, {});

    const payload = Object.values(itemModifications || {});
    dispatch({ type: 'SET_PRODUCT_MODS', payload });
  };

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    if (state.productData.id && state.selectedColor && state.selectedColor !== state.productData.id) {
      getProductData(state.selectedColor, true);
    }
  }, [state.selectedColor]);

  useEffect(() => {
    getColorData();
    if (state.productMods.length === 0) {
      setProductMods();
    }
  }, [state.productData?.id]);

  useEffect(() => setDefaultMods(), [state.productMods]);

  useEffect(() => {
    if (!forPdf && hash() && state.productData?.id && isModsLoaded) {
      debounceSaveData(
        state.productData,
        state.colorData,
        state.selectedColor,
        state.selectedMarking,
        state.selectedMods,
        state.userData,
      );
    }
  }, [
    state.productData,
    state.colorData,
    state.selectedColor,
    state.selectedMarking,
    state.selectedMods,
    state.userData,
  ]);

  if (isLoading) {
    return (
      <div style={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <i className="fas fa-2xl fa-circle-notch fa-spin"></i>
        <span style={{ marginLeft: '.5em' }}>{trans('Wczytywanie')}...</span>
      </div>
    );
  }
  return <MyContext.Provider value={[state, dispatch]}>{children}</MyContext.Provider>;
};

Store.propTypes = {
  children: PropTypes.node,
};

export const MyContext = createContext(initialState);

export default Store;
